import { useEffect, useState } from "react";
import type { Lang } from "../lib/i18n";
import { pick } from "../lib/i18n";
import { getVisibleSystems, subscribeSystems, systemName, systemCategory, systemDesc } from "../data/systems";
import { SystemCard } from "../components/SystemCard";
import { QRCode } from "../components/QRCode";

interface SystemsScreenProps {
  lang: Lang;
  careerPath: string;
  onNext: () => void;
  onBack: () => void;
}

/** Step 6 — systems carousel for the chosen path, plus an awareness grid. */
export function SystemsScreen({ lang, careerPath, onNext, onBack }: SystemsScreenProps) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [systems, setSystems] = useState(() => getVisibleSystems());
  useEffect(() => subscribeSystems(() => setSystems(getVisibleSystems())), []);

  const forPath = systems.filter((s) => s.paths.includes("all") || s.paths.includes(careerPath));
  const others = systems.filter((s) => !s.paths.includes("all") && !s.paths.includes(careerPath));
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (index >= forPath.length) setIndex(0);
  }, [forPath.length, index]);

  if (forPath.length === 0) {
    return (
      <div className="kd-step-container" dir={lang === "ar" ? "rtl" : "ltr"}>
        <div className="glass-panel step-systems">
          <p style={{ textAlign: "center", padding: "2rem" }}>{t("لا توجد أنظمة معروضة لهذا المسار حالياً.", "No systems are currently available for this path.")}</p>
          <div className="kd-step-nav-row">
            {onBack && <button className="kd-btn-secondary" onClick={onBack}><span>{t("السابق", "Back")}</span></button>}
            <button className="kd-btn-primary" onClick={onNext}><span>{t("متابعة", "Continue")}</span></button>
          </div>
        </div>
      </div>
    );
  }

  const current = forPath[index];
  const isLast = index === forPath.length - 1;
  const isFirst = index === 0;
  const goNext = () => (isLast ? onNext() : setIndex((i) => Math.min(i + 1, forPath.length - 1)));
  const goPrev = () => (isFirst ? onBack?.() : setIndex((i) => Math.max(i - 1, 0)));

  return (
    <div className="kd-step-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      <div className="glass-panel step-systems">
        <div className="kd-step-header kd-step-header-centered">
          <h1>{t("خريطة الأنظمة", "Systems Map")}</h1>
          <p>
            {t(
              "تستعرض هذه الصفحة الأنظمة المؤسسية المعتمدة في كادن، وتهدف إلى مساعدتك على فهم الغرض من كل نظام وآلية الوصول إليه وفق الصلاحيات التي يتطلبها دورك الوظيفي. لطلب صلاحية الوصول إلى أي نظام، يُرجى التواصل مع مديرك المباشر أو رفع طلب عبر نظام تقنية المعلومات. كما تجدر الإشارة إلى أن بعض الأنظمة متاحة أيضاً عبر تطبيقات الجوال، والتي يمكن الوصول إليها من أسفل كل نظام.",
              "This page presents the institutional systems used at Kaden, and is meant to help you understand each system's purpose and how to access it according to the permissions your role requires. To request access to a specific system, please contact your direct manager or submit a request through the IT system. Note that some systems are also available as mobile apps, which you can find below each system.",
            )}
          </p>
        </div>

        <div className="kd-sys-counter">{t(`النظام ${index + 1} من ${forPath.length}`, `System ${index + 1} of ${forPath.length}`)}</div>

        <div className="kd-sys-card-row">
          <button
            className="kd-sys-side-arrow kd-sys-side-arrow-prev kd-sys-side-arrow-lg"
            onClick={goPrev}
            aria-label={isFirst ? t("السابق، أسئلة القسم", "Back, Department FAQ") : t("السابق", "Previous")}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <polyline points={lang === "ar" ? "9 18 15 12 9 6" : "15 18 9 12 15 6"} />
            </svg>
            <span className="kd-sys-side-arrow-label">{isFirst ? t("السابق، أسئلة القسم", "Back, Department FAQ") : t("السابق", "Back")}</span>
          </button>

          <SystemCard sys={current} lang={lang} />

          <button className="kd-sys-side-arrow kd-sys-side-arrow-next kd-sys-side-arrow-lg" onClick={goNext} aria-label={isLast ? t("إنهاء، التالي", "Finish, Next") : t("التالي", "Next")}>
            <span className="kd-sys-side-arrow-label">{isLast ? t("إنهاء", "Finish") : t("التالي", "Next")}</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <polyline points={lang === "ar" ? "15 18 9 12 15 6" : "9 18 15 12 9 6"} />
            </svg>
          </button>
        </div>

        <div className="kd-sys-dots">
          {forPath.map((_, i) => (
            <button key={i} className={`kd-sys-dot${i === index ? " active" : ""}${i < index ? " done" : ""}`} onClick={() => setIndex(i)} aria-label={`System ${i + 1}`} />
          ))}
        </div>

        {others.length > 0 && (
          <div className="kd-sys-awareness">
            <div className="kd-sys-awareness-head">
              <h2>{t("أنظمة أخرى قد تحتاج التعرف عليها", "Other systems you may need to know about")}</h2>
            </div>
            <div className="kd-sys-awareness-grid">
              {others.map((s) => (
                <div className="kd-sys-awareness-card" style={{ borderColor: s.color + "33" }} key={s.id}>
                  <div className="kd-sys-awareness-icon" style={{ background: s.bgColor }}>
                    <img src={s.icon} alt={systemName(s, lang)} />
                  </div>
                  <div className="kd-sys-awareness-body">
                    <h3>{systemName(s, lang)}</h3>
                    <span className="kd-sys-awareness-cat" style={{ color: s.color }}>{systemCategory(s, lang)}</span>
                    <p>{systemDesc(s, lang)}</p>
                    {s.url && (
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="kd-sys-awareness-link" style={{ color: s.color, borderColor: s.color + "55" }}>
                        {t("فتح بالدخول الموحّد", "Open with SSO")} ↗
                      </a>
                    )}
                    {s.hasApp && (s.ios || s.android) && (
                      <div className="kd-sys-awareness-app">
                        <span className="kd-sys-awareness-app-label">{t("متاح كتطبيق جوال", "Available as a mobile app")}</span>
                        <div className="kd-sys-app-links">
                          {s.ios && (
                            <div className="kd-sys-store-col">
                              <div className="kd-sys-qr-wrap">
                                <QRCode url={s.ios} size={56} />
                              </div>
                              <a href={s.ios} target="_blank" rel="noopener noreferrer" className="kd-sys-store-btn">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                                </svg>
                                <span>App Store</span>
                              </a>
                            </div>
                          )}
                          {s.android && (
                            <div className="kd-sys-store-col">
                              <div className="kd-sys-qr-wrap">
                                <QRCode url={s.android} size={56} />
                              </div>
                              <a href={s.android} target="_blank" rel="noopener noreferrer" className="kd-sys-store-btn">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M3.18 23.04c.17.3.51.46.86.4l7.96-4.58-2.01-1.1-6.81 5.28zm-.18-1.36L9.98 12 3 2.32v19.36zm17.82-10.69L18.1 9.5l-3.38 1.95L18.1 13.5l2.72-1.57.43-.25a.75.75 0 0 0 0-1.3l-.43-.24zM3.53.98c-.37-.07-.73.1-.9.43L10.35 9.5l2.33-1.35L3.53.98zM14.6 10.95L12.27 12.3l2.33 1.35 3.37-1.95-3.37-1.94v.19z" />
                                </svg>
                                <span>Google Play</span>
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="kd-inline-note">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <span>
            {t(
              "لا يُشترط تسجيل الدخول في جميع الأنظمة الآن، المهم هو الاطلاع عليها والتعرّف على وظائفها.",
              "You are not required to sign into all systems at this time, the objective is to review and familiarize yourself with their functions.",
            )}
          </span>
        </div>
      </div>
    </div>
  );
}
